import type { DebugLayer } from '../config/debug.config';
import { el } from '../ui/dom';
import type { DebugManager } from './DebugManager';

export type DebugStats = Readonly<Record<string, string | number | boolean | null>>;

export interface DebugPanelActions {
  readonly onGodMode: (enabled: boolean) => void;
  readonly onAddLife: () => void;
}

const LAYER_LABELS: Readonly<Record<DebugLayer, string>> = {
  laneGuides: 'Spuren',
  connectors: 'Connectoren',
  obstacleSpawnPoints: 'Obstacle-Spawnpoints',
  collectibleSpawnPoints: 'Collectible-Points',
  decorationSockets: 'Deko-Sockets',
  dynamicEventSockets: 'Event-Sockets',
  adSlots: 'AD-Slots',
  colliders: 'Collider',
};

/** DOM overlay with live stats and layer toggles (F3 / ` toggles debug mode). */
export class DebugPanel {
  private readonly element: HTMLDivElement;
  private readonly stats: HTMLDListElement;
  private readonly cells = new Map<string, HTMLElement>();

  constructor(parent: HTMLElement, debug: DebugManager, actions: DebugPanelActions) {
    this.element = el('div', 'debug-panel', undefined, parent);
    el('div', 'debug-title', 'DEBUG  ·  F3', this.element);
    this.stats = el('dl', 'debug-stats', undefined, this.element);

    const layers = el('div', 'debug-layers', undefined, this.element);
    for (const layer of Object.keys(LAYER_LABELS) as DebugLayer[]) {
      this.checkbox(layers, LAYER_LABELS[layer], debug.layer(layer), (checked) => debug.setLayer(layer, checked));
    }
    const tools = el('div', 'debug-tools', undefined, this.element);
    this.checkbox(tools, 'Unverwundbar', false, actions.onGodMode);
    const addLife = el('button', 'debug-button', '+1 Leben', tools);
    addLife.type = 'button';
    addLife.addEventListener('click', actions.onAddLife);

    // Clicks on the panel must not reach the canvas (would count as tap/swipe).
    this.element.addEventListener('pointerdown', (event) => event.stopPropagation());
    debug.onChange((d) => this.setVisible(d.enabled));
    this.setVisible(debug.enabled);
  }

  setVisible(visible: boolean): void {
    this.element.classList.toggle('is-visible', visible);
  }

  update(stats: DebugStats): void {
    for (const [key, value] of Object.entries(stats)) {
      let cell = this.cells.get(key);
      if (!cell) {
        el('dt', undefined, key, this.stats);
        cell = el('dd', undefined, '', this.stats);
        this.cells.set(key, cell);
      }
      const text = value === null ? '–' : typeof value === 'boolean' ? (value ? 'ja' : 'nein') : String(value);
      if (cell.textContent !== text) cell.textContent = text;
    }
  }

  private checkbox(parent: HTMLElement, label: string, checked: boolean, onChange: (checked: boolean) => void): void {
    const wrapper = el('label', 'debug-check', undefined, parent);
    const input = el('input', undefined, undefined, wrapper);
    input.type = 'checkbox';
    input.checked = checked;
    input.addEventListener('change', () => onChange(input.checked));
    el('span', undefined, label, wrapper);
  }
}
